/**
 * ConnectionStatus — non-blocking top banner that surfaces when the
 * browser reports it is offline. Auto-dismisses on reconnection.
 *
 * Uses the `online`/`offline` window events (already exposed via
 * useNetworkStatus). Kept dependency-free and very lightweight so it
 * can mount once at the app root with negligible cost.
 */
import { useEffect, useState } from "react";
import { Wifi, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";

export const ConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      setJustReconnected(true);
      window.setTimeout(() => setJustReconnected(false), 2000);
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (isOnline && !justReconnected) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed top-0 left-1/2 -translate-x-1/2 z-[150] mt-2 px-3 py-1.5 rounded-full",
        "text-xs font-medium shadow-lg backdrop-blur-md border",
        "flex items-center gap-2 transition-all duration-300",
        isOnline
          ? "bg-primary/90 text-primary-foreground border-primary/30"
          : "bg-destructive/90 text-destructive-foreground border-destructive/30",
      )}
    >
      {isOnline ? (
        <>
          <Wifi className="h-3.5 w-3.5" />
          <span>Back online</span>
        </>
      ) : (
        <>
          <WifiOff className="h-3.5 w-3.5 animate-pulse" />
          <span>Reconnecting…</span>
        </>
      )}
    </div>
  );
};

export default ConnectionStatus;
